import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import {
  getAllEmailTemplates,
  getEmailTemplateById,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
} from "../../Utils/apiServices";
import Button from "../Button";
import Card from "../Card";
import TextField from "../TextField";
import { Typography } from "../Typography";
import toast from "../../Utils/toastManager";

const USER_ROLES = ['student', 'mentor', 'donor', 'admin', 'ecosystem_partner', 'corporate_sponsor', 'sponsor', 'investor', 'program_team', 'volunteer'];

const EmailTemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    subject: "",
    body: "",
    template_code: "",
    from_email: "",
    user_role: "",
    is_active: true,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await getAllEmailTemplates();
      if (response.success && response?.data?.data) {
        setTemplates(
          Array.isArray(response.data?.data)
            ? response.data?.data
            : []
        );
      }
    } catch (error) {
      console.error("Error fetching email templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({
      title: "",
      subject: "",
      body: "",
      template_code: "",
      from_email: "",
      user_role: "",
      is_active: true,
    });
  };

  const handleEdit = async (template) => {
    try {
      // Fetch full template details if needed
      const response = await getEmailTemplateById(template.id);
      if (response.success && response.data?.data) {
        const templateData = response.data.data;
        setEditingId(template.id);
        setIsCreating(false);
        setFormData({
          title: templateData.title || "",
          subject: templateData.subject || "",
          body: templateData.body || "",
          template_code: templateData.template_code || "",
          from_email: templateData.from_email || "",
          user_role: templateData.user_role || "",
          is_active: templateData.is_active !== undefined ? templateData.is_active : true,
        });
      } else {
        // Fallback to the template data we already have
        setEditingId(template.id);
        setIsCreating(false);
        setFormData({
          title: template.title || "",
          subject: template.subject || "",
          body: template.body || "",
          template_code: template.template_code || "",
          from_email: template.from_email || "",
          user_role: template.user_role || "",
          is_active: template.is_active !== undefined ? template.is_active : true,
        });
      }
    } catch (error) {
      console.error("Error fetching template details:", error);
      // Fallback to the template data we already have
      setEditingId(template.id);
      setIsCreating(false);
      setFormData({
        title: template.title || "",
        subject: template.subject || "",
        body: template.body || "",
        template_code: template.template_code || "",
        from_email: template.from_email || "",
        user_role: template.user_role || "",
        is_active: template.is_active !== undefined ? template.is_active : true,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormData({
      title: "",
      subject: "",
      body: "",
      template_code: "",
      from_email: "",
      user_role: "",
      is_active: true,
    });
  };

  const handleSave = async (templateId) => {
    try {
      const response = await updateEmailTemplate(templateId, formData);
      if (response.success) {
        await fetchTemplates();
        handleCancelEdit();
      } else {
        toast.error("Failed to update template: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("An error occurred while saving the template");
    }
  };

  const handleCreateSubmit = async () => {
    try {
      const response = await createEmailTemplate(formData);
      if (response.success) {
        await fetchTemplates();
        handleCancelEdit();
      } else {
        toast.error("Failed to create template: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error("An error occurred while creating the template");
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this email template?")) {
      return;
    }

    try {
      const response = await deleteEmailTemplate(templateId);
      if (response.success) {
        await fetchTemplates();
      } else {
        toast.error("Failed to delete template: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("An error occurred while deleting the template");
    }
  };

  // Filter templates based on search term
  const filteredTemplates = useMemo(() => {
    if (!searchTerm.trim()) return templates;
    
    const searchLower = searchTerm.toLowerCase();
    return templates.filter((template) => {
      const title = (template.title || "").toLowerCase();
      const subject = (template.subject || "").toLowerCase();
      const templateCode = (template.template_code || "").toLowerCase();
      const body = (template.body || "").replace(/<[^>]*>/g, "").toLowerCase();
      const fromEmail = (template.from_email || "").toLowerCase();
      const userRole = (template.user_role || "").toLowerCase();
      
      return (
        title.includes(searchLower) ||
        subject.includes(searchLower) ||
        templateCode.includes(searchLower) ||
        body.includes(searchLower) ||
        fromEmail.includes(searchLower) ||
        userRole.includes(searchLower)
      );
    });
  }, [templates, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading email templates...
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <Typography as="h2" className="text-2xl font-semibold text-primary-900">
          Email Templates
        </Typography>
        <Button
          onClick={handleCreate}
          startIcon={<Icon icon="lucide:plus" className="h-4 w-4" />}
        >
          Create Template
        </Button>
      </div>

      <Card 
        className="border border-slate-200 overflow-hidden"
        header={
          <div className="flex items-center justify-end w-full">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-48 px-3 py-1.5 pl-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
              <Icon
                icon="lucide:search"
                className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400"
              />
            </div>
          </div>
        }
      >
        {templates.length === 0 ? (
          <div className="p-6 text-center">
            <Typography as="p" className="text-tertiary-600">
              No email templates found.
            </Typography>
          </div>
        ) : (
          <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Template Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Body Preview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    From Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    User Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {isCreating && (
                  <tr className="bg-slate-50">
                    <td colSpan="7" className="px-6 py-4">
                      <div className="space-y-4">
                        <Typography
                          as="h3"
                          className="text-base font-semibold text-primary-900 mb-4"
                        >
                          Create Email Template
                        </Typography>
                        <div className="grid grid-cols-1 gap-4">
                          <TextField
                            name="title"
                            label="Title"
                            type="text"
                            value={formData.title}
                            onChange={(e) =>
                              handleInputChange("title", e.target.value)
                            }
                            placeholder="Enter template title"
                            required
                          />
                          <TextField
                            name="template_code"
                            label="Template Code"
                            type="text"
                            value={formData.template_code}
                            onChange={(e) =>
                              handleInputChange("template_code", e.target.value)
                            }
                            placeholder="Enter template code"
                          />
                          <TextField
                            name="subject"
                            label="Subject"
                            type="text"
                            value={formData.subject}
                            onChange={(e) =>
                              handleInputChange("subject", e.target.value)
                            }
                            required
                            placeholder="Enter email subject"
                          />
                          <div>
                            <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                              From Email
                            </label>
                            <select
                              name="from_email"
                              value={formData.from_email || ""}
                              onChange={(e) =>
                                handleInputChange("from_email", e.target.value)
                              }
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-slate-900 text-sm"
                            >
                              <option value="">Select from email</option>
                              <option value="contact@ignitenext.org">contact@ignitenext.org</option>
                              <option value="noreply@ignitenext.org">noreply@ignitenext.org</option>
                              <option value="admin@ignitenext.org">admin@ignitenext.org</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                              User Type
                            </label>
                            <select
                              name="user_role"
                              value={formData.user_role || ""}
                              onChange={(e) =>
                                handleInputChange("user_role", e.target.value)
                              }
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-slate-900 text-sm"
                            >
                              <option value="">Select user type</option>
                              {USER_ROLES.map((role) => (
                                <option key={role} value={role}>
                                  {role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ')}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                              Body <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              name="body"
                              value={formData.body}
                              onChange={(e) =>
                                handleInputChange("body", e.target.value)
                              }
                              required
                              placeholder="Enter email body"
                              rows={6}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) =>
                                  handleInputChange("is_active", e.target.checked)
                                }
                                className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                              />
                              <span className="text-sm font-medium text-primary-900">
                                Active
                              </span>
                            </label>
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-3">
                          <Button variant="secondary" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                          <Button onClick={handleCreateSubmit}>
                            Create
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {filteredTemplates.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center">
                      <Typography as="p" className="text-tertiary-600">
                        No email templates found matching your search.
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  filteredTemplates.map((template) => (
                  <React.Fragment key={template.id}>
                    {editingId === template.id ? (
                      <tr className="bg-slate-50">
                        <td colSpan="7" className="px-6 py-4">
                          <div className="space-y-4">
                            <Typography
                              as="h3"
                              className="text-base font-semibold text-primary-900 mb-4"
                            >
                              Edit Email Template
                            </Typography>
                            <div className="grid grid-cols-1 gap-4">
                              <TextField
                                name="title"
                                label="Title"
                                type="text"
                                value={formData.title}
                                onChange={(e) =>
                                  handleInputChange("title", e.target.value)
                                }
                                placeholder="Enter template title"
                                required
                              />
                              <TextField
                                name="template_code"
                                label="Template Code"
                                type="text"
                                value={formData.template_code}
                                onChange={(e) =>
                                  handleInputChange("template_code", e.target.value)
                                }
                                placeholder="Enter template code"
                              />
                              <TextField
                                name="subject"
                                label="Subject"
                                type="text"
                                value={formData.subject}
                                onChange={(e) =>
                                  handleInputChange("subject", e.target.value)
                                }
                                required
                                placeholder="Enter email subject"
                              />
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  From Email
                                </label>
                                <select
                                  name="from_email"
                                  value={formData.from_email || ""}
                                  onChange={(e) =>
                                    handleInputChange("from_email", e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-slate-900 text-sm"
                                >
                                  <option value="">Select from email</option>
                                  <option value="contact@ignitenext.org">contact@ignitenext.org</option>
                                  <option value="noreply@ignitenext.org">noreply@ignitenext.org</option>
                                  <option value="admin@ignitenext.org">admin@ignitenext.org</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  User Type
                                </label>
                                <select
                                  name="user_role"
                                  value={formData.user_role || ""}
                                  onChange={(e) =>
                                    handleInputChange("user_role", e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-slate-900 text-sm"
                                >
                                  <option value="">Select user type</option>
                                  {USER_ROLES.map((role) => (
                                    <option key={role} value={role}>
                                      {role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ')}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Body <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                  name="body"
                                  value={formData.body}
                                  onChange={(e) =>
                                    handleInputChange("body", e.target.value)
                                  }
                                  required
                                  placeholder="Enter email body"
                                  rows={6}
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) =>
                                      handleInputChange("is_active", e.target.checked)
                                    }
                                    className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                                  />
                                  <span className="text-sm font-medium text-primary-900">
                                    Active
                                  </span>
                                </label>
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-3">
                              <Button variant="secondary" onClick={handleCancelEdit}>
                                Cancel
                              </Button>
                              <Button onClick={() => handleSave(template.id)}>
                                Save
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900"
                            title={template.title}
                          >
                            {template.title || "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900"
                            title={template.template_code}
                          >
                            {template.template_code || "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm font-medium text-primary-900 line-clamp-1"
                            title={template.subject}
                          >
                            {template.subject || "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm text-tertiary-600 line-clamp-2"
                            title={template.body}
                          >
                            {template.body
                              ? template.body.replace(/<[^>]*>/g, "").substring(0, 100)
                              : "-"}
                            {template.body &&
                              template.body.replace(/<[^>]*>/g, "").length > 100 &&
                              "..."}
                          </Typography>
                        </td>
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900"
                            title={template.from_email}
                          >
                            {template.from_email || "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900"
                            title={template.user_role}
                          >
                            {template.user_role 
                              ? template.user_role.charAt(0).toUpperCase() + template.user_role.slice(1).replace(/_/g, ' ')
                              : "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleEdit(template)}
                              startIcon={<Icon icon="lucide:edit" className="h-4 w-4" />}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleDelete(template.id)}
                              startIcon={
                                <Icon icon="lucide:trash-2" className="h-4 w-4" />
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default EmailTemplateList;
